<template>
    <ElementLayout>
        <template #element>
            <div class="quote-fulllist">
                Can't find your shop?
                <a href="#" class="quote-fulllist-link" @click.prevent="open = true">Choose from the full list</a>
            </div>

            <teleport to="body">
                <div v-if="open" class="quote-fulllist-backdrop" @click.self="close">
                    <div class="quote-fulllist-modal" role="dialog" aria-modal="true" aria-label="Activities">
                        <div class="qfl-head">
                            <h4>Activities</h4>
                            <button type="button" class="qfl-close" aria-label="Close" @click="close">&times;</button>
                        </div>
                        <div class="qfl-search">
                            <input ref="searchInput" type="text" v-model="query" placeholder="Search activities…" />
                        </div>
                        <div class="qfl-alpha">
                            <button type="button" :class="{ on: !activeLetter }" @click="activeLetter = ''">All</button>
                            <button v-for="l in letters" :key="l" type="button" :class="{ on: activeLetter === l }" @click="toggleLetter(l)">{{ l }}</button>
                        </div>
                        <ul class="qfl-list">
                            <li v-for="item in filtered" :key="item.value">
                                <a href="#" @click.prevent="choose(item)">{{ item.label }}</a>
                            </li>
                            <li v-if="!filtered.length" class="qfl-empty">No matching activities</li>
                        </ul>
                    </div>
                </div>
            </teleport>
        </template>

        <template v-for="(component, slot) in elementSlots" #[slot]>
            <slot :name="slot" :el$="el$">
                <component :is="component" :el$="el$" />
            </slot>
        </template>
    </ElementLayout>
</template>

<script>
import { defineElement } from '@vueform/vueform'
import { ref, computed, inject, nextTick, watch } from 'vue'

export default defineElement({
    name: 'ActivityFullListElement',
    emits: ['activitySelected'],

    props: {
        items: { type: Array, default: () => [] },
    },

    setup(props, { element }) {
        const vueformApi = inject('vueformApi', null)
        const open = ref(false)
        const query = ref('')
        const activeLetter = ref('')
        const searchInput = ref(null)

        // Shops already chosen in the other InsuredShops row — hidden here too, matching the dropdown.
        const excludedIds = computed(() => {
            const rows = vueformApi?.value?.model?.InsuredShops
            if (!Array.isArray(rows)) return new Set()
            const path = typeof element.path === 'string' ? element.path : (element.path?.value ?? '')
            const ownIdx = Number(/InsuredShops\.(\d+)/.exec(path)?.[1] ?? -1)
            return new Set(rows
                .map((r, i) => (i === ownIdx ? NaN : Number(r?.ShopId)))
                .filter((id) => Number.isFinite(id)))
        })

        const sorted = computed(() =>
            [...props.items]
                .filter((i) => i && i.label != null && !excludedIds.value.has(Number(i.value)))
                .sort((a, b) => String(a.label).localeCompare(String(b.label))))

        const letters = computed(() =>
            [...new Set(sorted.value.map((i) => String(i.label)[0]?.toUpperCase()).filter((c) => /[A-Z]/.test(c)))])

        const filtered = computed(() => {
            const q = query.value.trim().toLowerCase()
            return sorted.value.filter((i) => {
                const label = String(i.label)
                if (activeLetter.value && label[0]?.toUpperCase() !== activeLetter.value) return false
                if (q && !label.toLowerCase().includes(q)) return false
                return true
            })
        })

        const toggleLetter = (l) => { activeLetter.value = activeLetter.value === l ? '' : l }

        const close = () => { open.value = false }

        const choose = (item) => {
            element.fire('activitySelected', item.value, element, vueformApi?.value)
            open.value = false
            query.value = ''
            activeLetter.value = ''
        }

        // Focus the search box when the modal opens.
        watch(open, (isOpen) => { if (isOpen) nextTick(() => searchInput.value?.focus()) })

        return { open, query, activeLetter, letters, filtered, toggleLetter, close, choose, searchInput }
    },
})
</script>
